import React, { useState, useEffect } from 'react'
import { WizardState, ExamOption } from '../Index'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from '@/components/ui/select'
import {
  Award, AlertTriangle, CheckCircle2,
  Calculator, PlusCircle, MinusCircle, Sparkles, Sliders, Check
} from 'lucide-react'

interface Props {
  state: WizardState
  updateState: (patch: Partial<WizardState>) => void
  exams?: ExamOption[]
}

export default function StepMarkingScheme({ state, updateState, exams = [] }: Props) {
  const scheme = state.markingScheme
  const selectedExam = exams.find(e => e.id === state.exam_id)
  const configuredRules = selectedExam?.marking_rules || []
  const isPractice = state.testType === 'practice'

  const activeSections = (state.blueprintSections || []).filter((bs: any) => bs._selected !== false)
  const hasSectionTemplates = activeSections.length > 0

  // Local text input state for seamless decimal typing (e.g. ".25", "0.33", "0.66")
  const [posStr, setPosStr] = useState<string>(
    scheme.positive_marks !== undefined ? String(scheme.positive_marks) : '1'
  )
  const [negStr, setNegStr] = useState<string>(
    scheme.negative_marks !== undefined ? String(scheme.negative_marks) : '0.25'
  )

  const selectedTestTypeObj = selectedExam?.test_types?.find(tt => tt.test_type_key === state.testType)
  const preconfiguredRuleId = selectedTestTypeObj?.exam_marking_rule_id
  const preconfiguredRule = selectedTestTypeObj?.marking_rule || configuredRules.find(r => r.id === preconfiguredRuleId)

  // If a preconfigured rule exists for this test type, show only that preconfigured blueprint rule + Custom.
  // Otherwise, fall back to all configured rules for the exam.
  const displayedPresetRules = preconfiguredRule 
    ? [preconfiguredRule] 
    : (configuredRules.length > 0 ? configuredRules : [])

  // Auto-select the preconfigured marking rule or first configured rule if none selected
  useEffect(() => {
    if (scheme.selected_rule_id === undefined) {
      if (preconfiguredRule) {
        applyConfiguredRule(preconfiguredRule)
      } else if (configuredRules.length > 0) {
        applyConfiguredRule(configuredRules[0])
      }
    }
  }, [configuredRules, preconfiguredRule])

  useEffect(() => {
    if (scheme.positive_marks !== undefined && String(scheme.positive_marks) !== posStr && posStr !== '' && !posStr.endsWith('.')) {
      setPosStr(String(scheme.positive_marks))
    }
  }, [scheme.positive_marks])

  useEffect(() => {
    if (scheme.negative_marks !== undefined && String(scheme.negative_marks) !== negStr && negStr !== '' && !negStr.endsWith('.')) {
      setNegStr(String(scheme.negative_marks))
    }
  }, [scheme.negative_marks])

  const parseNum = (val: string): number => {
    if (!val || val.trim() === '') return 0
    const parsed = parseFloat(val)
    return isNaN(parsed) ? 0 : parsed
  }

  const applyConfiguredRule = (rule: NonNullable<ExamOption['marking_rules']>[0]) => {
    const params = rule.scoring_params || {}
    const pos = params.correct ?? params.full_correct ?? 1
    const rawWrong = params.wrong ?? params.any_wrong ?? (rule.scoring_strategy === 'no_penalty' ? 0 : -0.25)
    const wrongVal = Math.abs(Number(rawWrong) || 0)
    const negEnabled = rule.scoring_strategy !== 'no_penalty' && wrongVal > 0

    setPosStr(String(pos))
    setNegStr(String(wrongVal))

    updateState({
      totalMarks: state.totalQuestions * pos,
      markedForReviewRule: (rule as any).marked_for_review_rule || state.markedForReviewRule || 'counts_if_answered',
      markingScheme: {
        ...scheme,
        selected_rule_id: rule.id,
        rule_name: rule.name,
        positive_marks: pos,
        negative_marks: wrongVal,
        negative_marking_enabled: negEnabled,
        scoring_strategy: rule.scoring_strategy,
        scoring_params: params
      }
    })
  }

  const handleSectionRuleChange = (sectionId: number, ruleIdStr: string) => {
    const sectionRules = { ...(scheme.section_marking_rules || {}) }
    if (ruleIdStr === 'global') {
      sectionRules[sectionId] = 'global' as any
    } else if (!ruleIdStr) {
      delete sectionRules[sectionId]
    } else {
      sectionRules[sectionId] = parseInt(ruleIdStr, 10)
    }

    // Calculate updated total marks taking section-specific rules into account
    let calculatedTotalMarks = 0
    activeSections.forEach((sec: any) => {
      const overrideVal = sectionRules[sec.id]
      const effectiveRuleId = overrideVal === 'global' ? null : (overrideVal ?? sec.marking_rule_id ?? scheme.selected_rule_id)
      const assignedRule = effectiveRuleId && effectiveRuleId !== 'custom' ? configuredRules.find(r => r.id === effectiveRuleId) : null
      const secPos = assignedRule
        ? (assignedRule.scoring_params?.correct ?? assignedRule.scoring_params?.full_correct ?? scheme.positive_marks)
        : (scheme.positive_marks || 1)
      calculatedTotalMarks += (sec.total_questions || 0) * secPos
    })

    updateState({
      totalMarks: calculatedTotalMarks > 0 ? calculatedTotalMarks : (state.totalQuestions * (scheme.positive_marks || 1)),
      markingScheme: {
        ...scheme,
        section_marking_rules: sectionRules
      }
    })
  }

  const applyExamMarkingRuleToCustom = (rule: NonNullable<ExamOption['marking_rules']>[0]) => {
    const params = rule.scoring_params || {}
    const pos = params.correct ?? params.full_correct ?? 1
    const rawWrong = params.wrong ?? params.any_wrong ?? (rule.scoring_strategy === 'no_penalty' ? 0 : -0.25)
    const wrongVal = Math.abs(Number(rawWrong) || 0)
    const negEnabled = rule.scoring_strategy !== 'no_penalty' && wrongVal > 0

    setPosStr(String(pos))
    setNegStr(String(wrongVal))

    updateState({
      totalMarks: state.totalQuestions * pos,
      markingScheme: {
        ...scheme,
        selected_rule_id: 'custom',
        rule_name: `Custom (from ${rule.name})`,
        positive_marks: pos,
        negative_marks: wrongVal,
        negative_marking_enabled: negEnabled,
        scoring_strategy: rule.scoring_strategy,
        scoring_params: params
      }
    })
  }

  const handleSelectCustom = () => {
    updateState({
      markingScheme: {
        ...scheme,
        selected_rule_id: 'custom',
        rule_name: 'Custom Marking Scheme'
      }
    })
  }

  const handlePositiveMarksChange = (valStr: string) => {
    setPosStr(valStr)
    const posMarks = Math.max(0, parseNum(valStr))
    const updatedTotalMarks = state.totalQuestions * posMarks
    updateState({
      totalMarks: updatedTotalMarks,
      markingScheme: {
        ...scheme,
        selected_rule_id: 'custom',
        positive_marks: posMarks
      }
    })
  }

  const handleNegativeMarksChange = (valStr: string) => {
    setNegStr(valStr)
    const negMarks = Math.max(0, parseNum(valStr))
    updateState({
      markingScheme: {
        ...scheme,
        selected_rule_id: 'custom',
        negative_marks: negMarks
      }
    })
  }

  const handleNegativeToggle = (enabled: boolean) => {
    const defaultNeg = enabled ? (parseNum(negStr) || 0.25) : 0
    if (enabled && (!negStr || parseNum(negStr) <= 0)) {
      setNegStr('0.25')
    }
    updateState({
      markingScheme: {
        ...scheme,
        selected_rule_id: 'custom',
        negative_marking_enabled: enabled,
        negative_marks: defaultNeg
      }
    })
  }

  const isPositiveInvalid = !scheme.positive_marks || scheme.positive_marks <= 0
  const isNegativeInvalid = scheme.negative_marking_enabled && (!scheme.negative_marks || scheme.negative_marks <= 0)
  const hasErrors = isPositiveInvalid || isNegativeInvalid

  const isCustomSelected = scheme.selected_rule_id === 'custom' || (!scheme.selected_rule_id && configuredRules.length === 0)

  // Calculate score breakdown taking section template rules into account
  let computedMaxPositive = 0
  let computedMaxPenalty = 0

  const hasActiveSectionOverrides = Boolean(
    scheme.section_marking_rules && Object.keys(scheme.section_marking_rules).length > 0
  )

  if (hasSectionTemplates && hasActiveSectionOverrides) {
    activeSections.forEach((sec: any) => {
      const overrideVal = scheme.section_marking_rules?.[sec.id]
      const effectiveRuleId = overrideVal === 'global' ? null : (overrideVal ?? sec.marking_rule_id ?? scheme.selected_rule_id)
      const assignedRule = effectiveRuleId && effectiveRuleId !== 'custom' ? configuredRules.find(r => r.id === effectiveRuleId) : null
      const p = assignedRule?.scoring_params || {}
      const pos = assignedRule ? (p.correct ?? p.full_correct ?? scheme.positive_marks) : (scheme.positive_marks || 1)
      const rawNeg = assignedRule ? (p.wrong ?? p.any_wrong ?? (assignedRule.scoring_strategy === 'no_penalty' ? 0 : -(scheme.negative_marks || 0.25))) : (scheme.negative_marking_enabled ? -(scheme.negative_marks || 0.25) : 0)
      const neg = Math.abs(Number(rawNeg) || 0)
      const negActive = assignedRule ? (assignedRule.scoring_strategy !== 'no_penalty' && neg > 0) : (scheme.negative_marking_enabled && neg > 0)

      computedMaxPositive += (sec.total_questions || 0) * pos
      if (negActive) {
        computedMaxPenalty += (sec.total_questions || 0) * neg
      }
    })
  } else {
    computedMaxPositive = state.totalQuestions * (scheme.positive_marks || 0)
    if (scheme.negative_marking_enabled) {
      computedMaxPenalty = state.totalQuestions * (scheme.negative_marks || 0)
    }
  }

  const maxPositiveMarks = computedMaxPositive.toFixed(2).replace(/\.00$/, '')
  const maxNegativePenalty = computedMaxPenalty > 0 ? computedMaxPenalty.toFixed(2).replace(/\.00$/, '') : '0'

  const enableSectionMarking = hasActiveSectionOverrides

  // Handle toggling Section Level Marking
  const handleToggleSectionMarking = (enabled: boolean) => {
    if (!enabled) {
      // Clear section overrides to revert all to global
      updateState({
        totalMarks: state.totalQuestions * (scheme.positive_marks || 1),
        markingScheme: {
          ...scheme,
          section_marking_rules: {}
        }
      })
    } else {
      // Populate defaults from active sections
      const initialRules: Record<number, any> = {}
      let calculatedTotalMarks = 0
      activeSections.forEach((sec: any) => {
        const assignedRuleId = sec.marking_rule_id || scheme.selected_rule_id
        initialRules[sec.id] = assignedRuleId || 'global'
        const assignedRule = assignedRuleId && assignedRuleId !== 'custom' ? configuredRules.find(r => r.id === assignedRuleId) : null
        const pos = assignedRule
          ? (assignedRule.scoring_params?.correct ?? assignedRule.scoring_params?.full_correct ?? scheme.positive_marks)
          : (scheme.positive_marks || 1)
        calculatedTotalMarks += (sec.total_questions || 0) * pos
      })

      updateState({
        totalMarks: calculatedTotalMarks > 0 ? calculatedTotalMarks : (state.totalQuestions * (scheme.positive_marks || 1)),
        markingScheme: {
          ...scheme,
          section_marking_rules: initialRules
        }
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Practice Mode: Locked Marking Banner */}
      {isPractice && (
        <div className="flex items-start gap-3 p-3.5 rounded-xl bg-primary/5 border border-primary/20 text-xs">
          <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-foreground">Practice Mode — Simplified Scoring</p>
            <p className="text-muted-foreground mt-0.5">
              Practice sets use positive marks only. Negative marking is <strong>disabled</strong> automatically
              and cannot be changed for this test type.
            </p>
          </div>
        </div>
      )}

      {/* Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2 text-foreground">
            <Award className="h-5 w-5 text-primary" />
            Marking Scheme & Evaluation Rules
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Configure test-wide grading rules or enable section-specific marking for multi-section exams
          </p>
        </div>
      </div>

      {hasErrors && (
        <div className="p-3.5 bg-destructive/10 border border-destructive/20 rounded-xl flex items-center gap-2.5 text-xs text-destructive font-medium">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>Please provide valid positive and penalty marks greater than 0 before continuing.</span>
        </div>
      )}
      {/* Main Grid: Left 2 Columns (All Configurations) + Right 1 Column (Evaluation Summary) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left 2 Columns: Full Configuration Stack */}
        <div className="lg:col-span-2 space-y-5">
          {/* ─── 1. GLOBAL MARKING SCHEME SELECTOR ─────────────────────────── */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-4">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Award className="h-4 w-4 text-primary shrink-0" /> Marking Scheme Mode
              </Label>
              {selectedExam && (
                <span className="text-[11px] text-muted-foreground font-medium break-words">
                  Target Exam: <strong className="text-foreground">{selectedExam.name}</strong>
                </span>
              )}
            </div>

            {/* Global Rule Selector Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {/* Preconfigured Rule from Selected Test Type or Exam Rules */}
              {displayedPresetRules.map(rule => {
                const isSelected = scheme.selected_rule_id === rule.id
                const p = rule.scoring_params || {}
                const correctVal = p.correct ?? p.full_correct ?? 1
                const wrongVal = p.wrong ?? p.any_wrong ?? (rule.scoring_strategy === 'no_penalty' ? 0 : -0.25)
                const unattemptedVal = p.unattempted ?? 0

                return (
                  <div
                    key={rule.id}
                    onClick={() => applyConfiguredRule(rule)}
                    className={`relative rounded-2xl p-3.5 sm:p-4 cursor-pointer transition-all border text-left flex flex-col justify-between space-y-3 ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-glass-xs'
                        : 'border-border/80 bg-card hover:border-primary/50 hover:bg-muted/30 shadow-2xs'
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 ${
                            isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'border border-border bg-muted/40 text-muted-foreground'
                          }`}>
                            {isSelected ? <Check className="h-3 w-3 stroke-[3]" /> : 'R'}
                          </div>
                          <span className="text-xs font-bold text-foreground break-words leading-snug">
                            {rule.name}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                          <Badge variant="outline" className="text-[9px] uppercase font-semibold px-1.5 py-0 h-4 bg-muted text-foreground border-border">
                            {rule.question_type}
                          </Badge>
                          <Badge variant="outline" className="text-[9px] uppercase font-semibold px-1.5 py-0 h-4 bg-primary/10 text-primary border-primary/20">
                            {rule.scoring_strategy}
                          </Badge>
                        </div>
                      </div>

                      {/* Param Pills */}
                      <div className="flex flex-wrap gap-1.5 pt-0.5 text-[10px] sm:text-[11px]">
                        <span className="px-1.5 sm:px-2 py-0.5 rounded-md font-semibold bg-primary/10 text-primary border border-primary/20">
                          +{correctVal} Correct
                        </span>
                        <span className="px-1.5 sm:px-2 py-0.5 rounded-md font-semibold bg-destructive/10 text-destructive border border-destructive/20">
                          {wrongVal !== 0 ? `${wrongVal}` : '0'} Wrong
                        </span>
                        <span className="px-1.5 sm:px-2 py-0.5 rounded-md font-medium bg-muted text-foreground border border-border/50">
                          {unattemptedVal} Unattempted
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-2 border-t border-border/40 font-medium">
                      <span>Exam Blueprint Scheme</span>
                      {isSelected && (
                        <span className="text-primary font-bold flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" /> Active
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}

              {/* Custom Marking Scheme Option Card */}
              <div
                onClick={handleSelectCustom}
                className={`relative rounded-2xl p-3.5 sm:p-4 cursor-pointer transition-all border text-left flex flex-col justify-between space-y-3 ${
                  isCustomSelected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-glass-xs'
                    : 'border-border/80 bg-card hover:border-primary/50 hover:bg-muted/30 shadow-2xs'
                }`}
              >
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5 ${
                        isCustomSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'border border-border bg-muted/40 text-muted-foreground'
                      }`}>
                        {isCustomSelected ? <Check className="h-3 w-3 stroke-[3]" /> : <Sliders className="h-3 w-3" />}
                      </div>
                      <span className="text-xs font-bold text-foreground break-words leading-snug">
                        Custom Marking Scheme
                      </span>
                    </div>

                    <Badge variant="outline" className="text-[9px] uppercase font-bold px-1.5 py-0 h-4 bg-muted/50 border-primary/30 text-primary shrink-0">
                      Custom
                    </Badge>
                  </div>

                  <p className="text-[11px] text-muted-foreground leading-relaxed pt-0.5">
                    Set custom correct response points and negative penalty values below.
                  </p>
                </div>

                <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-2 border-t border-border/40 font-medium">
                  <span>Manual Configuration</span>
                  {isCustomSelected && (
                    <span className="text-primary font-bold flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Active
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ─── 2. SCORING INPUTS OR BLUEPRINT BANNER ───────────────────── */}
          {isCustomSelected ? (
            /* Custom Marking Input Controls with Radix UI Select Dropdown */
            <div className={`bg-card border ${enableSectionMarking ? 'opacity-90 border-border/80' : 'border-border/80'} rounded-2xl p-5 space-y-5 shadow-glass-xs transition-all`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-border/60 pb-4">
                <div className="flex items-start gap-2.5">
                  <div className="h-8 w-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                    <Calculator className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Label className="text-xs font-bold uppercase tracking-wider text-foreground">
                        {enableSectionMarking ? 'Default / Fallback Scoring Rules' : 'Custom Test Scoring Rules'}
                      </Label>
                      {enableSectionMarking && (
                        <Badge variant="outline" className="text-[9px] uppercase font-bold bg-muted text-muted-foreground border-border">
                          Fallback
                        </Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {enableSectionMarking
                        ? 'Applied to any general questions not covered by section-specific rules below.'
                        : 'Configure correct response points and negative penalty values.'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5 shrink-0 self-start md:self-auto">
                  {!isPractice && configuredRules.length > 0 && (
                    <Select
                      onValueChange={(val) => {
                        const rule = configuredRules.find(r => String(r.id) === val)
                        if (rule) applyExamMarkingRuleToCustom(rule)
                      }}
                    >
                      <SelectTrigger className="h-8 w-48 text-xs font-medium bg-background border-border rounded-xl">
                        <div className="flex items-center gap-1.5 truncate text-muted-foreground">
                          <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                          <SelectValue placeholder="Load Exam Scheme..." />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-border bg-popover text-foreground">
                        {configuredRules.map(rule => (
                          <SelectItem key={rule.id} value={String(rule.id)} className="text-xs py-2 cursor-pointer">
                            <div className="flex items-center justify-between gap-3 w-full">
                              <span className="font-semibold text-foreground">{rule.name}</span>
                              <Badge variant="outline" className="text-[8px] uppercase px-1 py-0 h-3.5 bg-muted text-muted-foreground border-border">
                                {rule.question_type}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/20 shrink-0 h-8 px-2.5 flex items-center">
                    +{scheme.positive_marks} / {scheme.negative_marking_enabled ? `-${scheme.negative_marks}` : '0'} pts
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {/* Positive Marking Section */}
                <div className="space-y-2 p-3.5 rounded-xl bg-muted/20 border border-border/60">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="positive-marks" className="text-xs font-bold text-foreground flex items-center gap-1.5">
                      <PlusCircle className="h-3.5 w-3.5 text-primary" /> Correct Response Reward
                    </Label>
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Input
                        id="positive-marks"
                        type="number"
                        step="any"
                        min="0"
                        placeholder="e.g. 4 or 1"
                        required
                        value={posStr}
                        onChange={e => handlePositiveMarksChange(e.target.value)}
                        className={`h-9 text-xs font-bold w-32 rounded-xl bg-background ${isPositiveInvalid ? 'border-destructive focus-visible:ring-destructive' : 'border-border/70'}`}
                      />
                      <span className="text-xs font-semibold text-muted-foreground">pts / Q</span>
                    </div>
                    {isPositiveInvalid ? (
                      <p className="text-[11px] font-medium text-destructive flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 shrink-0" /> Compulsory mark &gt; 0 required
                      </p>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">
                        Awards <strong className="text-foreground">+{scheme.positive_marks || 0} marks</strong> for each correct answer.
                      </p>
                    )}
                  </div>
                </div>

                {/* Negative Marking Section */}
                <div className="space-y-2 p-3.5 rounded-xl bg-muted/20 border border-border/60">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="negative-toggle" className="text-xs font-bold text-foreground flex items-center gap-1.5">
                      <MinusCircle className="h-3.5 w-3.5 text-destructive" /> Negative Penalty
                    </Label>
                    <Switch
                      id="negative-toggle"
                      checked={isPractice ? false : scheme.negative_marking_enabled}
                      onCheckedChange={isPractice ? undefined : handleNegativeToggle}
                      disabled={isPractice}
                    />
                  </div>

                  <div className="space-y-1.5">
                    {!isPractice && scheme.negative_marking_enabled ? (
                      <>
                        <div className="flex items-center gap-2">
                          <Input
                            id="negative-marks"
                            type="number"
                            step="any"
                            min="0"
                            placeholder="e.g. 0.25 or 1"
                            required
                            value={negStr}
                            onChange={e => handleNegativeMarksChange(e.target.value)}
                            className={`h-9 text-xs font-bold w-32 rounded-xl bg-background ${isNegativeInvalid ? 'border-destructive focus-visible:ring-destructive text-destructive' : 'border-border/70 text-destructive'}`}
                          />
                          <span className="text-xs font-semibold text-muted-foreground">penalty / Q</span>
                        </div>
                        {isNegativeInvalid ? (
                          <p className="text-[11px] font-medium text-destructive flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3 shrink-0" /> Compulsory penalty &gt; 0 required
                          </p>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">
                            Deducts <strong className="text-destructive">-{scheme.negative_marks || 0} marks</strong> for each wrong response.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[11px] text-muted-foreground italic pt-2">
                        {isPractice
                          ? 'Disabled in practice mode.'
                          : 'Negative marking is currently disabled (0 penalty).'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Marked for Review Rule Section */}
                <div className="space-y-2 p-3.5 rounded-xl bg-muted/20 border border-border/60">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                      <Award className="h-3.5 w-3.5 text-primary" /> Marked for Review Rule
                    </Label>
                  </div>

                  <div className="space-y-1.5">
                    <Select
                      value={state.markedForReviewRule || 'counts_if_answered'}
                      onValueChange={(val) => updateState({ markedForReviewRule: val })}
                    >
                      <SelectTrigger className="w-full h-9 px-3 rounded-xl border border-border bg-background text-xs font-medium focus:ring-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50">
                        <SelectItem value="counts_if_answered">Counts if Answered (NTA)</SelectItem>
                        <SelectItem value="never_counts">Never Counts (GATE)</SelectItem>
                        <SelectItem value="always_counts">Always Counts</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                      Defines whether marked-for-review answers are evaluated during scoring.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Blueprint Locked Info Banner */
            <div className="bg-card border border-border/80 rounded-2xl p-4 sm:p-5 shadow-glass-xs flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <div className="sm:hidden flex-1 min-w-0">
                  <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/25">
                    Locked to Exam Standard
                  </Badge>
                </div>
              </div>

              <div className="space-y-1.5 flex-1 min-w-0 w-full">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-xs sm:text-sm font-bold text-foreground break-words">
                    Using Official Blueprint: {scheme.rule_name || 'Standard Exam Scheme'}
                  </h4>
                  <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/25 shrink-0 hidden sm:inline-flex">
                    Locked to Exam Standard
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed break-words">
                  Marking rules are standardized per the <strong>{selectedExam?.name}</strong> exam blueprint (+{scheme.positive_marks} on correct, {scheme.negative_marking_enabled ? `-${scheme.negative_marks}` : '0'} on wrong).
                  To set custom positive and negative marks, click <strong>"Custom Marking Scheme"</strong> above.
                </p>
              </div>
            </div>
          )}

          {/* ─── 3. SECTION-LEVEL MARKING OVERRIDE (WITH TOGGLE) ──────────────────── */}
          {hasSectionTemplates && (
            <div className={`p-3.5 sm:p-5 rounded-2xl space-y-3.5 sm:space-y-4 shadow-glass-xs transition-all ${
              enableSectionMarking
                ? 'bg-card border-2 border-primary/40 ring-1 ring-primary/20'
                : 'bg-muted/20 border border-border/70'
            }`}>
              <div className="space-y-2.5 border-b border-border/60 pb-3">
                {/* Title and Toggle row: Full width on mobile, inline on desktop */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Sliders className={`h-4 w-4 shrink-0 ${enableSectionMarking ? 'text-primary' : 'text-muted-foreground'}`} />
                    <Label htmlFor="section-marking-toggle" className="text-xs font-bold uppercase tracking-wider text-foreground cursor-pointer break-words flex-1">
                      Section-Level Marking Rules
                    </Label>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-2.5 w-full sm:w-auto pt-1 sm:pt-0 border-t sm:border-t-0 border-border/40">
                    <span className="text-[11px] font-medium text-muted-foreground sm:hidden">
                      {enableSectionMarking ? `${activeSections.length} Sections Active` : 'Global Mode'}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className={`hidden sm:inline-flex text-[10px] font-bold transition-colors ${enableSectionMarking ? 'bg-primary/10 text-primary border-primary/30' : 'bg-muted text-muted-foreground'}`}>
                        {enableSectionMarking ? `${activeSections.length} Sections Active` : 'Global Mode'}
                      </Badge>
                      <Switch
                        id="section-marking-toggle"
                        checked={enableSectionMarking}
                        onCheckedChange={handleToggleSectionMarking}
                      />
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-muted-foreground leading-relaxed w-full">
                  {enableSectionMarking
                    ? 'Section rules below override the global scheme and will be used to evaluate student responses for each respective section.'
                    : 'Disabled: All sections currently follow the global test marking scheme above.'}
                </p>
              </div>

              {/* Show section cards ONLY when enabled */}
              {enableSectionMarking && (
                <div className="space-y-3 pt-1">
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl flex items-center gap-2 text-xs text-primary font-medium">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>Per-section marking is active. Each question will be graded according to its assigned paper section rule below:</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    {activeSections.map((sec: any) => {
                      const overrideVal = scheme.section_marking_rules?.[sec.id]
                      const effectiveRuleId = overrideVal === 'global' ? null : (overrideVal ?? sec.marking_rule_id ?? scheme.selected_rule_id)
                      const currentRule = effectiveRuleId && effectiveRuleId !== 'custom' ? configuredRules.find(r => r.id === effectiveRuleId) : null
                      const p = currentRule?.scoring_params || {}
                      const pos = currentRule ? (p.correct ?? p.full_correct ?? scheme.positive_marks) : scheme.positive_marks
                      const rawWrong = currentRule ? (p.wrong ?? p.any_wrong ?? (currentRule?.scoring_strategy === 'no_penalty' ? 0 : -scheme.negative_marks)) : (scheme.negative_marking_enabled ? -scheme.negative_marks : 0)
                      const wrongVal = Math.abs(Number(rawWrong) || 0)

                      const selectVal = overrideVal !== undefined ? String(overrideVal) : (sec.marking_rule_id ? String(sec.marking_rule_id) : 'global')

                      return (
                        <div key={sec.id} className="p-3.5 sm:p-4 bg-background border border-border/80 rounded-2xl space-y-3 shadow-2xs">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-bold text-foreground break-words flex-1">{sec.name}</span>
                            <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/20 shrink-0">
                              {sec.total_questions} Questions
                            </Badge>
                          </div>

                          <div className="space-y-1.5">
                            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                              Assigned Marking Rule
                            </Label>
                            <Select
                              value={selectVal}
                              onValueChange={(val) => handleSectionRuleChange(sec.id, val)}
                            >
                              <SelectTrigger className="h-9 w-full text-xs font-medium bg-card border-border rounded-xl">
                                <SelectValue placeholder="Select marking rule" />
                              </SelectTrigger>
                              <SelectContent className="rounded-xl border-border bg-popover text-foreground">
                                <SelectItem value="global" className="text-xs py-2 cursor-pointer font-medium">
                                  Use Global Scheme (+{scheme.positive_marks || 0} / {scheme.negative_marking_enabled ? `-${scheme.negative_marks}` : '0'})
                                </SelectItem>
                                {configuredRules.map(rule => (
                                  <SelectItem key={rule.id} value={String(rule.id)} className="text-xs py-2 cursor-pointer">
                                    <div className="flex items-center justify-between gap-2 w-full">
                                      <span className="font-semibold">{rule.name}</span>
                                      <Badge variant="outline" className="text-[8px] uppercase px-1 py-0 h-3.5 bg-muted text-muted-foreground border-border">
                                        {rule.question_type}
                                      </Badge>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex items-center justify-between text-[11px] pt-2 border-t border-border/40 font-mono flex-wrap gap-1">
                            <span className="text-primary font-bold">+{pos} Correct</span>
                            <span className={wrongVal > 0 ? 'text-destructive font-bold' : 'text-muted-foreground font-medium'}>
                              {wrongVal > 0 ? `-${wrongVal} Penalty` : 'No Penalty'}
                            </span>
                            <span className="text-foreground font-semibold">= {(sec.total_questions * pos).toFixed(0)} Marks</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column: Calculated Score Breakdown Summary (Full Height Sidebar) */}
        <div>
          {/* Mobile View: Compact Consolidated Card (lg:hidden) */}
          <div className="lg:hidden bg-card border border-border/80 rounded-2xl p-3.5 shadow-glass-xs space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
              <span className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-2">
                <Calculator className="h-4 w-4 text-primary" /> Evaluation Summary
              </span>
              <Badge variant="outline" className="text-[10px] font-bold bg-primary/10 text-primary border-primary/20">
                {enableSectionMarking ? 'Section-Specific' : (scheme.scoring_strategy?.replace('_', ' ') || 'Global')}
              </Badge>
            </div>

            {/* Active Scheme Strip */}
            <div className="p-2.5 bg-primary/5 border border-primary/20 rounded-xl flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <span className="text-[9px] font-bold uppercase tracking-wider text-primary block">
                  Active Strategy
                </span>
                <div className="text-xs font-bold text-foreground break-words leading-tight mt-0.5">
                  {enableSectionMarking
                    ? 'Multi-Section Customized Scoring'
                    : (scheme.rule_name || (isCustomSelected ? 'Custom Marking Scheme' : 'Standard Scheme'))
                  }
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground shrink-0 font-mono text-right">
                {state.totalQuestions} Qs
              </div>
            </div>

            {/* Unified Metrics Bar (3 Columns) */}
            <div className="p-2.5 rounded-xl bg-muted/40 border border-border/60 grid grid-cols-3 divide-x divide-border/60 text-center text-xs">
              <div className="px-1 space-y-0.5">
                <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider truncate">Max Score</div>
                <div className="font-black text-foreground text-sm">+{maxPositiveMarks}</div>
                <div className="text-[9px] text-muted-foreground/80 font-mono">marks</div>
              </div>
              <div className="px-1 space-y-0.5">
                <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider truncate">Penalty</div>
                <div className={`font-black text-sm ${Number(maxNegativePenalty) > 0 ? 'text-destructive' : 'text-foreground'}`}>
                  {Number(maxNegativePenalty) > 0 ? `-${maxNegativePenalty}` : '0'}
                </div>
                <div className="text-[9px] text-muted-foreground/80 font-mono">
                  {Number(maxNegativePenalty) > 0 ? 'penalty' : 'disabled'}
                </div>
              </div>
              <div className="px-1 space-y-0.5">
                <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider truncate">Range</div>
                <div className="font-bold text-primary text-xs pt-0.5">
                  {Number(maxNegativePenalty) > 0 ? `-${maxNegativePenalty}` : '0'} to +{maxPositiveMarks}
                </div>
                <div className="text-[9px] text-muted-foreground/80 font-mono">range</div>
              </div>
            </div>
          </div>

          {/* Desktop View: Full-height Rich Sidebar (hidden lg:block) */}
          <div className="hidden lg:block bg-card border border-border/80 rounded-2xl p-5 shadow-glass-xs space-y-4 sticky top-4">
            <span className="text-xs font-bold uppercase tracking-wider text-foreground flex items-center gap-2 border-b border-border/60 pb-3">
              <Calculator className="h-4 w-4 text-primary" /> Evaluation Summary
            </span>

            {/* Active Scheme Pill */}
            <div className="p-3 bg-primary/10 border border-primary/20 rounded-xl space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-primary">
                Active Marking Strategy
              </span>
              <div className="text-xs font-bold text-foreground">
                {enableSectionMarking
                  ? 'Multi-Section Customized Scoring'
                  : (scheme.rule_name || (isCustomSelected ? 'Custom Marking Scheme' : 'Standard Scheme'))
                }
              </div>
              <div className="text-[10px] text-muted-foreground capitalize">
                Mode: <strong>{enableSectionMarking ? 'Section-Specific' : (scheme.scoring_strategy?.replace('_', ' ') || 'Global')}</strong>
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-3.5 bg-primary/5 border border-primary/20 rounded-xl space-y-1">
                <span className="text-[11px] font-bold text-primary">
                  Maximum Attainable Score
                </span>
                <div className="text-2xl font-black text-foreground">
                  +{maxPositiveMarks} <span className="text-xs font-normal text-muted-foreground">Marks</span>
                </div>
                <p className="text-[11px] text-muted-foreground font-mono">
                  {state.totalQuestions} Questions total
                </p>
              </div>

              <div className="p-3.5 bg-destructive/10 border border-destructive/20 rounded-xl space-y-1">
                <span className="text-[11px] font-bold text-destructive">
                  Maximum Possible Penalty
                </span>
                <div className="text-2xl font-black text-destructive">
                  {Number(maxNegativePenalty) > 0 ? `-${maxNegativePenalty}` : '0'} <span className="text-xs font-normal text-muted-foreground">Marks</span>
                </div>
                <p className="text-[11px] text-muted-foreground font-mono">
                  {scheme.negative_marking_enabled || enableSectionMarking
                    ? 'Penalty enforced on incorrect responses'
                    : 'Negative marking disabled'}
                </p>
              </div>

              <div className="p-3.5 bg-muted/40 border border-border/60 rounded-xl space-y-1">
                <span className="text-[11px] font-bold text-muted-foreground">
                  Score Dynamic Range
                </span>
                <div className="text-sm font-bold text-foreground">
                  {Number(maxNegativePenalty) > 0 ? `-${maxNegativePenalty}` : '0'} to +{maxPositiveMarks} Marks
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

